import { AnyAction } from "redux";

import { ConditionParams, IDataRowsParams } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { DEFAULT_DATA_COUNT_TO_LOAD } from "@multiweb/reactUtils";
import { IRowData } from "@multiweb/types/contracts";
import { IOrderBy, OrderType } from "@multiweb/types/multiweb";
import {
    MutableRecordType,
    MutableTypedObject,
    RecordType,
    RootNodeSubType,
    RootNodeTypedObject,
} from "@multiweb/types/react";
import { ISelectNextRecord, ISelectPreviousRecord, RecordIdKeyType } from "./types";

const { compareCaseInsensitive, readAttributeValue } = bridge;

/**
 * Převede typ IOrderBy na podmínku využívanou v dotazech na server.
 *
 * @param orderByItem Objekt řazení obsahující potřebné atributy (IOrderBy).
 */
export const orderByObjectToConditionString = (orderByItem: IOrderBy): string =>
    orderByItem.order === OrderType.ascending ? orderByItem.attributeName : `DESC ${orderByItem.attributeName}`;

/**
 * Převede pole objektů řazení (IOrderBy) na pole stringových podmínek pro dotaz na server.
 *
 * @param orderBy Kolekce objektů pro seřazení dat.
 * @param dataKey Identifikační klíč (např. "id", "kod", "jmeno").
 */
export const mapOrderByToString = (orderBy: ReadonlyArray<IOrderBy>, dataKey: string): string[] => {
    const result = orderBy.map((value) => orderByObjectToConditionString(value));
    if (!orderBy.find((v) => compareCaseInsensitive(v.attributeName, dataKey))) {
        result.push(dataKey);
    }
    return result;
};

/**
 * Spojí konfigurace do parametru pro OWManagera.
 *
 * @param startIndex Index, od kterého se data načítají.
 * @param fetchCount Počet načítaných dat.
 * @param dataKey Klíč k identifikátoru uvnitř záznamu.
 * @param orderBy Attributy, podle kterých se řadí data.
 * @param conditionParams Argumenty podmínky.
 */
export const getParameters = (
    startIndex: number,
    fetchCount: number,
    dataKey: string,
    orderBy: ReadonlyArray<IOrderBy>,
    conditionParams: ConditionParams,
): IDataRowsParams => {
    const stringOrderBy: string[] | undefined = mapOrderByToString(orderBy, dataKey);
    return {
        startIndex,
        count: fetchCount !== 0 ? fetchCount : DEFAULT_DATA_COUNT_TO_LOAD,
        orderBy: stringOrderBy,
        params: conditionParams,
    };
};

/**
 * Pomocná funkce pro namapování IRowData[] na IData[]. Z IAttrValue/IEncAttrValue ukládá pouze hodnoty (přes metodu *valueOf*).
 *
 * @param apiData Data, která přijdou z OWManagera.
 */
export const mapApiDataToStateData = (apiData: ReadonlyArray<IRowData>): RecordType[] =>
    apiData.map((apiRow) => {
        const newRecord: MutableRecordType = {};
        Object.keys(apiRow).forEach((attribute) => {
            const apiAtt = apiRow[attribute];
            newRecord[attribute.toLowerCase()] = { value: readAttributeValue(apiAtt) };
        });
        return newRecord;
    });

/**
 * Vrací přepnuté řazení.
 *
 * @param order Řazení, které metoda přepne.
 */
export const toggleOrderType = (order: OrderType | undefined): OrderType | undefined => {
    switch (order) {
        case OrderType.ascending:
            return OrderType.descending;
        case OrderType.descending:
            return undefined;
        default:
            return OrderType.ascending;
    }
};
/**
 * Selector pro získání identifikátoru ze záznamu.
 *
 * @param record Data z aplikačního serveru.
 * @param recordIdKey Klíč identifikátoru v záznamu.
 */
export const getRecordId = (record: RecordType | undefined, recordIdKey: RecordIdKeyType) =>
    record?.[recordIdKey]?.value.toString();

/**
 * Vrací předchozí položku v poli.
 *
 * @param items Pole, ve kterém má funkce hledat.
 * @param actualItem Aktuální položka, ke které hledáme souseda.
 * @returns Předchozí Položka. `undefined` pokud aktuální položka nebyla nalezena nebo byla jako první v poli.
 */
export const getPreviousItem = <T>(items: ReadonlyArray<T>, actualItem: T): T | undefined => {
    const previousItemIndex = items.findIndex((item) => item === actualItem) - 1;
    if (previousItemIndex < 0) {
        // -2 -> nenelezen, -1 -> aktualni radek je prvni
        return undefined;
    }
    return items[previousItemIndex];
};

/**
 * Vrací další položku v poli.
 *
 * @param items Pole, ve kterém má funkce hledat.
 * @param actualItem Aktuální položka, ke které hledáme sousední.
 * @returns Další položka. `undefined` pokud aktuální položka nebyla nalezena nebo byla jako poslední v poli.
 */
export const getNextItem = <T>(items: ReadonlyArray<T>, actualItem: T): T | undefined => {
    const nextRowIndex = items.findIndex((item) => item === actualItem) + 1;
    if (nextRowIndex < 1 || nextRowIndex === items.length) {
        // 0 -> nenelezen, rows.length -> aktualni radek je poslední
        return undefined;
    }
    return items[nextRowIndex];
};

/**
 * Výčet směrů, kterými se dá v DataSource pohybovat.
 */
export enum RecordMoveDirection {
    next = "next",
    previous = "previous",
}

/**
 * Vrací identifikátor nově označeného záznamu podle směru pohybu v DataSource.
 *
 * @param recordsIds Pole identifikátorů pro záznamy.
 * @param actualId Identifikátor aktuálního záznamu.
 * @param direction Směr pohybu po záznamech v DataSource.
 */
export const getRecordIdByDirection = <T>(
    recordsIds: ReadonlyArray<T>,
    actualId: T,
    direction?: RecordMoveDirection,
): T | undefined => {
    switch (direction) {
        case RecordMoveDirection.next: {
            return getNextItem(recordsIds, actualId);
        }
        case RecordMoveDirection.previous: {
            return getPreviousItem(recordsIds, actualId);
        }
    }
    return undefined;
};

/**
 * Získá směr pohybu v DataSource podle typu akce.
 *
 * @param action Akce, podle které se určí jakým směrem se má v DataSource pohybovat výběr.
 */
export const getSelectionDirectionFromAction = (action: AnyAction): RecordMoveDirection | undefined => {
    if (action instanceof ISelectNextRecord) {
        return RecordMoveDirection.next;
    } else if (action instanceof ISelectPreviousRecord) {
        return RecordMoveDirection.previous;
    }
    return undefined;
};

/**
 * Určuje, zda se na dataSource mohou načítat další data.
 *
 * @param requestedCount Počet dat, o který DataSource požádal.
 * @param loadedCount Počet reálně načtených dat.
 */
export const hasNextItems = (requestedCount: number, loadedCount: number) =>
    !(loadedCount < requestedCount || loadedCount === 0);

/**
 * Pomocná funkce pro procházení kořenových uzlů.
 * Pokud zaškrtnu vnitřní záznam a mám záznam i v jiných uzlech, musím je všechny zkontrolovat,
 * zda u nich nedojde ke kompletnímu zaškrtnutí vrcholového záznamu.
 *
 * @param draft Stav z reduceru.
 * @param localRootNodes Kořenové uzly k projití.
 * @param globalRootNodes Kořenové uzly z Reduxu.
 * @param checked Zda byl záznam zaškrtnut. Pokud ano, zjistím, jestli není potřebné zaškrtnout i vrcholový záznam.
 */
const handleRootNodes = (draft: MutableTypedObject<boolean>, localRootNodes: string[], globalRootNodes: RootNodeTypedObject, checked: boolean) => {
    for (const rootNode of localRootNodes) {
        if (!checked) {
            draft[rootNode] = checked; // Pokud byl vnitřní záznam odškrtnut, určitě nebude zaškrtnut vrcholový záznam
        } else {
            draft[rootNode] = globalRootNodes[rootNode].children.every(child => draft[child]);
        }
    }
};

/**
 * Projde kořenový uzel a jeho děti. Pokud jsou děti i v jiných vrcholových záznamech zjistí, jestli je bude potřeba taktéž zaškrtnout.
 *
 * @param draft Stav z reduceru.
 * @param rootNodes Kořenové uzly z Reduxu.
 * @param recordId Id zaškrtávaného záznamu.
 */
export const handleCheckingRootNodes = (draft: MutableTypedObject<boolean>, rootNodes: RootNodeTypedObject, recordId: string) => {
    const { children, inRootNodes } = rootNodes[recordId] ?? { children: [], inRootNodes: [] };

    if (rootNodes[recordId]) {
        for (const child of children) {
            if (rootNodes[child]) { // pokud potomek je vrcholový záznam nebo je v jiných záznamech
                toggleNodeCheck(draft, child, recordId);
                handleRootNodes(draft, rootNodes[child].inRootNodes.filter(id => id !== recordId), rootNodes, draft[child]);
            } else {
                toggleNodeCheck(draft, child, recordId);
            }
        }
    }

    if (inRootNodes?.length > 0) {
        handleRootNodes(draft, inRootNodes, rootNodes, draft[recordId]);
    }
};

/**
 * Pomocná funkce pro zaškrtnutí uzlu.
 *
 * @param draft Stav z reduceru.
 * @param id Id záznamu, který se bude zaškrtávat.
 * @param recordId Id rodičovského uzlu, podle kterého se vezme stav.
 */
const toggleNodeCheck = (draft: MutableTypedObject<boolean>, id: string, recordId: string) => {
    draft[id] = draft[recordId];
};

/**
 * Při zaslaní OwnerId, vím identifikaci rodiče. Můžu tak zkontrolovat, zda jsou zaškrtnuté všechny jeho děti a podle toho změnit jeho stav.
 *
 * @param draft Stav z reduceru.
 * @param rootNodes Kořenové uzly z Reduxu.
 * @param ownerId Rodič zaškrtávaného záznamu.
 */
export const handleCheckingWithOwnerId = (draft: MutableTypedObject<boolean>, rootNodes: RootNodeTypedObject, ownerId: string) => {
    const { children: otherChildren } = rootNodes[ownerId] ?? {};

    if (otherChildren?.length > 0) {
        draft[ownerId] = otherChildren.every(child => draft[child]);
    }
};

/**
 * Pomocná funkce pro zaškrtávání dětí uzlových záznamů.
 *
 * @param draft Stav z reduceru.
 * @param rootNode Kořenové uzel z Reduxu.
 * @param value hodnota na kterou se mají zaškrtnout.
 */
export const handleCheckingRootNodeChildren = (draft: MutableTypedObject<boolean>, rootNode: RootNodeSubType, value: boolean) => {
    if (rootNode) {
        for (const child of rootNode.children) {
            draft[child] = value;
        }
    }
};
